import { ALLOWED_IMAGE_MIME_TYPES, creatorBadge, type Creator } from "@coinfallsmp/shared";
import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useUploadsEnabled } from "../AppConfigContext.js";
import { creatorOf, useAuth } from "../AuthContext.js";
import {
  designateOfficial,
  disableCreator,
  enableCreator,
  getCreator,
  getCreatorAvatarUploadUrl,
  finalizeCreatorAvatar,
  resetCreatorPassword,
  setCreatorAdmin,
  updateCreator,
  uploadToPresignedUrl,
} from "../api.js";
import CreatorBadge from "../components/CreatorBadge.js";
import StatusBadge from "../components/StatusBadge.js";

export default function CreatorDetail() {
  const { id } = useParams<{ id: string }>();
  const { identity } = useAuth();
  const self = creatorOf(identity);
  const uploadsEnabled = useUploadsEnabled();
  const [creator, setCreator] = useState<Creator | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [temporaryPassword, setTemporaryPassword] = useState<string | null>(null);

  // Editable form state, synced from the loaded creator.
  const [displayName, setDisplayName] = useState("");
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [bio, setBio] = useState("");
  const [avatarUrl, setAvatarUrl] = useState("");

  const load = useCallback(async () => {
    if (!id) return;
    const c = await getCreator(id);
    setCreator(c);
    setDisplayName(c.displayName);
    setUsername(c.username);
    setEmail(c.email);
    setBio(c.bio ?? "");
    setAvatarUrl(c.avatarUrl ?? "");
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  if (!creator || !id) return <p className="status-message">Loading…</p>;

  async function saveProfile() {
    setBusy(true);
    setError(null);
    try {
      await updateCreator(id!, {
        displayName,
        username,
        email,
        bio: bio || null,
        avatarUrl: avatarUrl.trim() || null,
      });
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to save");
    } finally {
      setBusy(false);
    }
  }

  async function toggle(field: "status" | "canUpload" | "isVerified", value: unknown) {
    setBusy(true);
    setError(null);
    try {
      await updateCreator(id!, { [field]: value });
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to update");
    } finally {
      setBusy(false);
    }
  }

  async function onDesignateOfficial() {
    if (
      !confirm(
        `Make "${creator!.displayName}" the official CoinfallSMP account? This removes the crown 👑 from whichever account currently has it.`,
      )
    ) {
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await designateOfficial(id!);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to designate");
    } finally {
      setBusy(false);
    }
  }

  async function onAvatarChange(file: File | null) {
    if (!file) return;
    setBusy(true);
    setError(null);
    try {
      const { uploadUrl, key } = await getCreatorAvatarUploadUrl(id!, {
        contentType: file.type as (typeof ALLOWED_IMAGE_MIME_TYPES)[number],
        fileSizeBytes: file.size,
      });
      await uploadToPresignedUrl(uploadUrl, file);
      await finalizeCreatorAvatar(id!, key);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Avatar upload failed");
    } finally {
      setBusy(false);
    }
  }

  async function onToggleAdmin(nextIsAdmin: boolean) {
    setBusy(true);
    setError(null);
    try {
      await setCreatorAdmin(id!, nextIsAdmin);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to update admin access");
    } finally {
      setBusy(false);
    }
  }

  async function onToggleDisabled(nextDisabled: boolean) {
    setBusy(true);
    setError(null);
    try {
      if (nextDisabled) await disableCreator(id!);
      else await enableCreator(id!);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to update account status");
    } finally {
      setBusy(false);
    }
  }

  async function onResetPassword() {
    if (
      !confirm(
        `Generate a new temporary password for "${creator!.displayName}"? This signs them out everywhere.`,
      )
    ) {
      return;
    }
    setBusy(true);
    setError(null);
    setTemporaryPassword(null);
    try {
      const { temporaryPassword: temp } = await resetCreatorPassword(id!);
      setTemporaryPassword(temp);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to reset password");
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="form-page">
      <div className="page-header">
        <h1>
          <CreatorBadge badge={creatorBadge(creator)} /> {creator.displayName}
        </h1>
        <StatusBadge status={creator.status} />
      </div>

      <div className="detail-grid">
        <div>
          <label>
            Display name
            <input value={displayName} onChange={(e) => setDisplayName(e.target.value)} />
          </label>
          <label>
            Username (used in /creators/&lt;username&gt;)
            <input value={username} onChange={(e) => setUsername(e.target.value)} />
          </label>
          <label>
            Email (their authenticated identity)
            <input value={email} onChange={(e) => setEmail(e.target.value)} />
          </label>
          <label>
            Bio
            <textarea rows={4} value={bio} onChange={(e) => setBio(e.target.value)} />
          </label>
          <label>
            Avatar image URL
            <input
              type="url"
              value={avatarUrl}
              onChange={(e) => setAvatarUrl(e.target.value)}
              placeholder="https://cdn.discordapp.com/avatars/…"
              disabled={busy}
            />
          </label>
          {creator.avatarUrl ? (
            <img
              src={creator.avatarUrl}
              alt=""
              className="avatar-preview"
              referrerPolicy="no-referrer"
            />
          ) : null}
          {uploadsEnabled ? (
            <label>
              Or upload a file
              <input
                type="file"
                accept={ALLOWED_IMAGE_MIME_TYPES.join(",")}
                onChange={(e) => onAvatarChange(e.target.files?.[0] ?? null)}
                disabled={busy}
              />
            </label>
          ) : null}

          {error ? <p className="form-error">{error}</p> : null}

          <div className="button-row">
            <button className="button primary" onClick={saveProfile} disabled={busy}>
              Save profile
            </button>
          </div>
        </div>

        <div className="publish-targets">
          <h2>Permissions</h2>
          <div className="permission-row">
            <span>Status</span>
            <select
              value={creator.status}
              disabled={busy}
              onChange={(e) => toggle("status", e.target.value)}
            >
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="revoked">Revoked</option>
            </select>
          </div>
          <div className="permission-row">
            <span>Can upload</span>
            <label className="switch">
              <input
                type="checkbox"
                checked={creator.canUpload}
                disabled={busy}
                onChange={(e) => toggle("canUpload", e.target.checked)}
              />
              <span>{creator.canUpload ? "Enabled" : "Disabled"}</span>
            </label>
          </div>
          <div className="permission-row">
            <span>Verified (✓ badge)</span>
            <label className="switch">
              <input
                type="checkbox"
                checked={creator.isVerified}
                disabled={busy || creator.isOfficial}
                onChange={(e) => toggle("isVerified", e.target.checked)}
              />
              <span>{creator.isVerified ? "Verified" : "Not verified"}</span>
            </label>
          </div>

          <hr />

          {creator.isOfficial ? (
            <p className="form-hint">
              👑 This is the official CoinfallSMP account. Designate a different account as
              official below to move the crown elsewhere.
            </p>
          ) : (
            <p className="form-hint">
              Only one account can hold the crown 👑 at a time. This is a protected, deliberate
              action — it never happens as a side effect of the toggles above.
            </p>
          )}
          <button
            className="button secondary"
            onClick={onDesignateOfficial}
            disabled={busy || creator.isOfficial}
          >
            {creator.isOfficial ? "Already official" : "Designate as official CoinfallSMP account"}
          </button>
        </div>
      </div>

      <div className="publish-targets form-section">
        <h2>Account access</h2>

        <div className="permission-row">
          <span>Admin access</span>
          <label className="switch">
            <input
              type="checkbox"
              checked={creator.isAdmin}
              disabled={busy || creator.id === self?.id}
              onChange={(e) => onToggleAdmin(e.target.checked)}
            />
            <span>{creator.isAdmin ? "Admin" : "Not admin"}</span>
          </label>
        </div>
        {creator.id === self?.id ? (
          <p className="form-hint">You can't remove your own admin access.</p>
        ) : null}

        <div className="permission-row">
          <span>Account</span>
          <label className="switch">
            <input
              type="checkbox"
              checked={!creator.isDisabled}
              disabled={busy}
              onChange={(e) => onToggleDisabled(!e.target.checked)}
            />
            <span>{creator.isDisabled ? "Disabled" : "Enabled"}</span>
          </label>
        </div>

        <hr />

        <p className="form-hint">
          {creator.hasPassword
            ? "This account has a password set."
            : "This account has no password yet — it can't log in until one is issued."}
          {creator.mustResetPassword ? " A password reset is pending on next login." : ""}
        </p>
        <p className="form-hint">
          Generates a fresh temporary password, shown once below — relay it to the creator
          out-of-band (e.g. Discord). It is never stored or displayed again afterward.
        </p>
        <button className="button secondary" onClick={onResetPassword} disabled={busy}>
          Reset password
        </button>
        {temporaryPassword ? (
          <p className="form-hint">
            Temporary password (shown once): <code>{temporaryPassword}</code>
          </p>
        ) : null}
      </div>
    </section>
  );
}
